import useAppNavigation from '@/hooks/useAppNavigation';
import { capitalize, isNil } from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineTimer } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { JsonLd } from 'react-schemaorg';
import type { Service } from 'schema-dts';
import { formatHourToSchemaMarkupOpeningHourSpecification } from '../../../common/formatter';
import { ROUTES } from '../../../constants/Constants';
import useAvailableServicesModal from '../../../hooks/useAvailableServicesModal';
import { setCurrentPartnerBusinessService } from '../../../redux/ducks/PartnerSelectionDucks';
import type {
  PartnerBusinessDescriptor,
  PartnerDetailsDescriptor,
  PartnerServiceDescriptor,
} from '../../../redux/ducks/SearchDucks.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper';

interface PartnerSeoServicesProps {
  ssrFetchedData: AppSsrFetchedDataType;
  partnerProfile: PartnerDetailsDescriptor;
  partnerServices: PartnerServiceDescriptor[];
  selectedService: string | undefined;
  selectedBusiness: string | undefined;
}

const MAX_VISIBLE_SERVICES = 4;

const PartnerSeoServices: React.FC<PartnerSeoServicesProps> = ({
  ssrFetchedData,
  partnerProfile,
  partnerServices,
  selectedService,
  selectedBusiness,
}) => {
  const { t, i18n } = useTranslation();
  const servicesModal = useAvailableServicesModal();
  const dispatch = useDispatch();
  const { toLocalizedRoute } = useAppNavigation();

  const targetBusiness = useMemo<PartnerBusinessDescriptor | undefined>(() => {
    if (!isNil(selectedBusiness)) {
      const matchingBusinesses = partnerProfile.businesses.filter(
        (biz) => biz.active && biz.id === selectedBusiness,
      );
      if (matchingBusinesses.length > 0) {
        return matchingBusinesses[0];
      }
    }
  }, [partnerProfile, selectedBusiness]);

  const partnerRoute = useMemo<string>(() => {
    return toLocalizedRoute(
      ROUTES.PARTNER_PROFILE_ROUTE.replace(
        ':partnerPath',
        `${partnerProfile.path}`,
      ),
    );
  }, [partnerProfile, toLocalizedRoute]);

  const matchingServices = useMemo<PartnerServiceDescriptor[]>(() => {
    return partnerServices.filter(
      (s) =>
        s.active &&
        partnerProfile.businesses.filter(
          (b) =>
            b.id === selectedBusiness && b.active && b.services.includes(s.id),
        ).length > 0,
    );
  }, [partnerServices, selectedBusiness, partnerProfile.businesses]);

  const onServiceClick = useCallback(
    (service: PartnerServiceDescriptor) => {
      dispatch(setCurrentPartnerBusinessService(service));
    },
    [dispatch],
  );

  return (
    <div className="flex min-w-full flex-col items-start justify-start px-2">
      <div className="min-w-full">
        <span className="my-4 line-clamp-1 min-w-full overflow-hidden text-ellipsis break-all text-xl font-bold">
          {t('static.profile_page.available_services_header')}
        </span>
        {matchingServices.length > 0 ? (
          <div className="flex min-w-full flex-col items-start justify-start gap-3">
            {matchingServices
              .sort((a, b) => {
                if (a.default) {
                  return -100000;
                }
                if (b.default) {
                  return 100000;
                }

                return a.duration - b.duration;
              })
              .map((service, index) => {
                const selected: boolean =
                  !isNil(selectedService) && service.id === selectedService;

                return (
                  <button
                    id={`partner-page-quick-service-${service.id}`}
                    key={`partner-page-quick-service-${service.id}`}
                    aria-label={service.name}
                    data-test-id={`partner-page-quick-service-${service.id}`}
                    data-umami-event="partner-page-quick-service-selected"
                    data-umami-event-service={`service-${service.id}`}
                    className={`group ${
                      index < MAX_VISIBLE_SERVICES ? 'flex' : 'hidden'
                    } min-w-full flex-col rounded-lg p-2 ${
                      selected
                        ? 'bg-slate-100'
                        : 'bg-white cursor-pointer hover:border-slate-400'
                    } rounded-lg border border-solid border-slate-200`}
                    disabled={selected}
                    type="button"
                    onClick={() => {
                      onServiceClick(service);
                    }}
                  >
                    <span className="flex min-ww-full flex-row items-start justify-between border-b border-solid border-slate-200">
                      <span className="flex grow flex-col items-start justify-start">
                        <span className="line-clamp-1 w-full overflow-hidden text-ellipsis break-all text-left text-lg font-semibold capitalize">
                          {service.name}
                        </span>
                      </span>
                      <span className="min-w-16 text-right font-semibold">
                        {new Intl.NumberFormat(i18n.language, {
                          style: 'currency',
                          currency: service.currency,
                        }).format(service.price)}
                      </span>
                    </span>
                    <span className="mt-1 flex min-w-full flex-row items-start justify-start gap-3">
                      <span className="grow text-pretty text-sm flex flex-col text-start items-start justify-start">
                        {service?.description}
                      </span>
                      <span className="flex min-w-28 flex-row items-start justify-start">
                        <span className="line-clamp-1 flex h-full grow flex-col items-end justify-end overflow-hidden text-ellipsis break-all text-sm">
                          {t('static.profile_page.service_duration_label', {
                            duration: Math.floor(service.duration / 60),
                          })}
                        </span>
                        <span className="ml-0.5 flex h-full flex-col items-end justify-end">
                          <MdOutlineTimer size={16} />
                        </span>
                      </span>
                    </span>
                    <JsonLd<Service>
                      item={{
                        '@context': 'https://schema.org',
                        '@type': 'Service',
                        '@id': `${!import.meta.env.PROD ? 'http' : 'https'}://${
                          ssrFetchedData.domain
                        }/service/${service.id}`,
                        name: service.name,
                        description: service?.description,
                        provider: {
                          '@type': 'MedicalBusiness',
                          '@id': `${
                            !import.meta.env.PROD ? 'http' : 'https'
                          }://${ssrFetchedData.domain}/${
                            !isNil(targetBusiness)
                              ? `business/${targetBusiness.id}`
                              : `provider/${partnerProfile.id}`
                          }`,
                          name: `${partnerProfile.first_name} ${partnerProfile.last_name}`,
                          url: `${!import.meta.env.PROD ? 'http' : 'https'}://${
                            ssrFetchedData.domain
                          }${partnerRoute}`,
                          image: !isNil(partnerProfile?.media?.profile_picture)
                            ? `${
                                !import.meta.env.PROD ? 'http' : 'https'
                              }://${getEnvironmentVariable(
                                'REACT_APP_CDN_API',
                              )}${partnerProfile?.media?.profile_picture}`
                            : undefined,
                          address: !isNil(targetBusiness?.location)
                            ? {
                                '@type': 'PostalAddress',
                                addressCountry: targetBusiness.location.country,
                                postalCode: targetBusiness.location?.zipcode,
                                addressLocality: targetBusiness.location?.city,
                                addressRegion: targetBusiness.location?.state,
                                streetAddress: targetBusiness.location.address,
                              }
                            : undefined,
                          areaServed: !isNil(targetBusiness?.location)
                            ? {
                                '@type': 'City',
                                name: targetBusiness.location.city,
                              }
                            : undefined,
                          hasPOS: !isNil(targetBusiness?.location)
                            ? {
                                '@type': 'Place',
                                '@id': `${
                                  !import.meta.env.PROD ? 'http' : 'https'
                                }://${ssrFetchedData.domain}/place/${
                                  targetBusiness.id
                                }`,
                                geo: {
                                  '@type': 'GeoCoordinates',
                                  latitude: targetBusiness.location.latitude,
                                  longitude: targetBusiness.location.longitude,
                                },
                              }
                            : undefined,
                          location: !isNil(targetBusiness?.location)
                            ? {
                                '@type': 'Place',
                                '@id': `${
                                  !import.meta.env.PROD ? 'http' : 'https'
                                }://${ssrFetchedData.domain}/place/${
                                  targetBusiness.id
                                }`,
                                geo: {
                                  '@type': 'GeoCoordinates',
                                  latitude: targetBusiness.location.latitude,
                                  longitude: targetBusiness.location.longitude,
                                },
                              }
                            : undefined,
                          openingHoursSpecification: targetBusiness?.hours
                            ? [
                                ...targetBusiness.hours.map((h) =>
                                  formatHourToSchemaMarkupOpeningHourSpecification(
                                    h,
                                  ),
                                ),
                              ]
                            : undefined,
                        },
                        broker: {
                          '@type': 'Organization',
                          '@id': `${
                            !import.meta.env.PROD ? 'http' : 'https'
                          }://${
                            ssrFetchedData.domain
                          }/organization/${getEnvironmentVariable(
                            'REACT_APP_DOMAIN_CODE',
                          )}`,
                          name: ssrFetchedData.appConfiguration?.service.name,
                        },
                        areaServed: !isNil(targetBusiness?.location)
                          ? {
                              '@type': 'City',
                              name: targetBusiness.location.city,
                            }
                          : undefined,
                        category: !isNil(service?.category)
                          ? capitalize(t(`static.tags.${service.category}`))
                          : undefined,
                        offers: {
                          '@type': 'Offer',
                          '@id': `${
                            !import.meta.env.PROD ? 'http' : 'https'
                          }://${ssrFetchedData.domain}/offer/${
                            targetBusiness?.id
                          }_${service?.id}`,
                          price: service?.price,
                          priceCurrency: service?.currency,
                          availability:
                            'https://schema.org/LimitedAvailability',
                          itemOffered: {
                            '@type': 'Service',
                            '@id': `${
                              !import.meta.env.PROD ? 'http' : 'https'
                            }://${ssrFetchedData.domain}/service/${
                              service?.id
                            }`,
                            name: service?.name,
                            description: service?.description,
                          },
                        },
                        potentialAction: {
                          '@type': 'ReserveAction',
                          target: {
                            '@type': 'EntryPoint',
                            urlTemplate:
                              typeof window !== 'undefined'
                                ? window.location.href.split('?')[0]
                                : ssrFetchedData.url,
                            actionPlatform: [
                              'http://schema.org/DesktopWebPlatform',
                              'http://schema.org/IOSPlatform',
                              'http://schema.org/AndroidPlatform',
                              'http://schema.org/MobileWebPlatform',
                              'http://schema.org/GenericWebPlatform',
                            ],
                          },
                          result: {
                            '@type': 'Reservation',
                            name: t(
                              'static.cart.appointment_action_button_label',
                            ),
                          },
                        },
                      }}
                    />
                  </button>
                );
              })}
          </div>
        ) : (
          <div className="text-sm hover:font-semibold">
            {t('static.profile_page.available_services_empty_label')}
          </div>
        )}
        {matchingServices.length > MAX_VISIBLE_SERVICES && (
          <div className="min-w-full flex flex-col justify-center items-center my-2">
            <button
              id="partner-page-more-services"
              key="partner-page-more-services"
              aria-label={t('static.generic.show_more_services')}
              data-test-id="partner-page-more-services"
              data-umami-event="partner-page-more-services-selected"
              data-umami-event-source="quicklist"
              type="button"
              onClick={() => servicesModal.onOpen()}
              className="text-sm font-semibold underline rounded-lg my-2 p-2 cursor-pointer hover:bg-slate-100"
            >
              {t('static.generic.show_more_services')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerSeoServices;
